import {defineStore} from 'pinia';
import {DataService} from '../../api';
import {useNotificationsStore} from './notifications';
import {HitBannerNotificationItem} from '@hit/base/src/store/pinia/notifications';
import {translate} from '@hit/base/src/plugins/i18n/i18n';
import {globals} from 'hit-online-web-ui/src/globals';
import Axios from 'axios';
import {Base64} from 'js-base64';

const SUPPORT_MAIL_ADDRESSES = ['support@ficos.com', 'synchro@ficos.com'];

function moduleKeyToLogName(moduleKey) {
  if (typeof moduleKey !== 'string' || !moduleKey.trim()) return null;
  let name = moduleKey
    .trim()
    .replace(/-base$/, '')
    .replace(/-/g, '_')
    .toUpperCase();
  return name.length > 0 ? name : null;
}

export const useUserProfileStore = defineStore('userProfile', {
  state: () => ({
    uuid: '',
    hitOnlineStaffId: '',
    userObject: null,
    username: '',
    email: '',
    locale: 'en-GB',
    roles: [],
    modules: [],
    companiesCount: 0,
    moduleUsageLastSeen: {},
    userIsAdministrator: false,
  }),
  getters: {
    /**
     * Returns the keycloak ID of the connected user (loaded during mounted of App)
     */
    keycloakId(state) {
      return state.uuid;
    },

    /**
     * Returns the staffId of the connected user (loaded during mounting of App)
     */
    staffId(state) {
      return state.hitOnlineStaffId;
    },

    /**
     * Returns the staff object of the connected user
     */
    user(state) {
      return state.userObject;
    },

    isAdmin(state) {
      return state.userIsAdministrator;
    },

    /**
     * Returns the selected locale of the user in snake case format
     */
    localeSnakeCase(state) {
      return state.locale.replace('-', '_').toLowerCase();
    },

    isSupport(state) {
      return SUPPORT_MAIL_ADDRESSES.includes(state.email);
    },
  },
  actions: {
    /**
     * Method executed when the application is loaded to only load the staffId once
     */
    async initUserProfileStore() {
      this.uuid = globals.$appContext.user.id;
      this.username = `${globals.$appContext.user.firstName} ${globals.$appContext.user.lastName}`;
      this.userIsAdministrator = globals.$keycloak.tokenParsed.admin;
      return Promise.all([
        this.loadStaffId(),
        this.setKeycloakData(),
        this.loadModules(),
        this.loadCompaniesCount(),
      ]);
    },

    /**
     * Sets the staffID of the user
     */
    async loadStaffId() {
      const res = await DataService.read('staff', {
        attributes:
          'id,no,designation,first_name,last_name,staff_qualification(qualification_id)',
        filters: {
          user_id: `eq.${globals.$keycloak.idTokenParsed.sub}`,
        },
      });
      if (res && res.data && res.data.length > 0) {
        const {default: Staff} = await import('../../models/hit/Staff');
        this.hitOnlineStaffId = res.data[0].id;
        this.roles = res.data[0].staff_qualification.map(
          (item) => item.qualification_id,
        );
        this.userObject = new Staff(res.data[0]);
      } else {
        useNotificationsStore().insertBannerNotification(
          translate('hit-base.banners.link-to-staff'),
          HitBannerNotificationItem.WARNING,
        );
      }
    },

    syncStaffIdFromUserLink(userId, staffId) {
      if (userId === globals.$keycloak?.idTokenParsed?.sub) {
        this.hitOnlineStaffId = staffId || null;
      }
    },

    async loadModules() {
      try {
        // Fetch user modules
        const userUrl = `${process.env.VUE_APP_ADMIN_API_URL}user/data`;
        const userConfig = {
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Bearer ${globals.$keycloak.token}`,
          },
        };

        const userResponse = await Axios.get(userUrl, userConfig);
        const userModules = userResponse.data[0].modules;
        const userModulesSet = new Set(userModules.map((m) => m.module_id));

        // Fetch all standard modules and integrations
        const moduleUrl = `${process.env.VUE_APP_ADMIN_API_URL}module/list`;
        const integrationModuleUrl = `${process.env.VUE_APP_ADMIN_API_URL}module/list?integrationsOnly=true`;
        const concatCredentials = `${process.env.VUE_APP_ADMIN_API_BASIC_AUTH_USERNAME}:${process.env.VUE_APP_ADMIN_API_BASIC_AUTH_PASSWORD}`;
        const token = Base64.encode(concatCredentials);
        const moduleConfig = {
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Basic ${token}`,
          },
        };

        const [moduleResponse, integrationModuleResponse] = await Promise.all([
          Axios.get(moduleUrl, moduleConfig),
          Axios.get(integrationModuleUrl, moduleConfig),
        ]);
        const allModules = [
          ...(moduleResponse.data ?? []),
          ...(integrationModuleResponse.data ?? []),
        ];
        const modules = allModules.map((module) => ({
          ...module,
          selected: !module.editable ? true : userModulesSet.has(module.id),
          description_key: module.designation_key,
        }));

        // Assign to the store's state using $patch to avoid issues with the Proxy
        this.$patch((state) => {
          state.modules = modules;
        });
      } catch (error) {
        console.error('Error loading modules:', error);
      }
    },

    async setKeycloakData() {
      return new Promise((resolve, reject) => {
        try {
          this.uuid = globals.$keycloak.idTokenParsed.sub;
          this.email = globals.$keycloak.idTokenParsed.email;
          resolve(this.uuid);
        } catch (error) {
          reject(error);
        }
      });
    },

    setRoles(roles) {
      this.roles = roles;
    },

    /**
     * Checks if the user own the passed module to enable/disable features in UI
     */
    ownsModule(moduleName) {
      return this.modules.some(
        (obj) => obj['description_key'] === moduleName && obj['selected'],
      );
    },

    addRole(roleId) {
      this.roles.push(roleId);
    },

    removeRole(roleId) {
      this.roles = this.roles.filter((r) => r !== roleId);
    },

    /**
     * Function to check if the user has access to the passed module
     * @param {string} moduleDesignationKey - designation key of module in module table in hitonline database
     * @returns {boolean} - True if the user has access to the module, false otherwise
     */
    hasAccessToModule(moduleDesignationKey) {
      const module = this.modules?.find(
        (mod) => mod.designation_key === moduleDesignationKey,
      );
      return module?.selected ?? false;
    },

    async loadCompaniesCount() {
      try {
        const url = `${process.env.VUE_APP_ADMIN_API_URL}company/count-companies-by-user`;
        const config = {
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Bearer ${globals.$keycloak.token}`,
          },
        };

        const response = await Axios.get(url, config);
        this.companiesCount = response.data.count;
        return response.data.count;
      } catch (error) {
        console.error('Error loading companies count:', error);
        this.companiesCount = 0;
        return 0;
      }
    },

    moduleKeyToLogName(moduleKey) {
      return moduleKeyToLogName(moduleKey);
    },

    recordModuleUsage(moduleKeys) {
      if (!moduleKeys || !Array.isArray(moduleKeys)) return;
      const now = new Date().toISOString();
      this.$patch((state) => {
        for (const key of moduleKeys) {
          const name = moduleKeyToLogName(key);
          if (name) state.moduleUsageLastSeen[name] = now;
        }
      });
    },

    getModuleUsagePayload() {
      return {...this.moduleUsageLastSeen};
    },

    async sendModuleUsageAndReset(options = {}) {
      const payload = this.getModuleUsagePayload();
      const keys = Object.keys(payload);
      if (keys.length === 0) return;

      const baseUrl = (process.env.VUE_APP_ADMIN_API_URL || '').replace(
        /\/$/,
        '',
      );
      const url = `${baseUrl}/log/module-usage`;
      const body = JSON.stringify({module_usage: payload});
      const token = globals.$keycloak?.token;
      if (!token) return;

      try {
        const fetchOptions = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body,
          ...(options.keepalive && {keepalive: true}),
        };
        await fetch(url, fetchOptions);
      } catch (err) {
        console.error('Error sending module usage:', err);
      } finally {
        this.$patch((state) => {
          state.moduleUsageLastSeen = {};
        });
      }
    },
  },
});
